/**
 * Hygeon inline search overlay. Uses Shopify's public predictive-search
 * JSON endpoint (/search/suggest.json) directly, since our custom header
 * forms were never built on Horizon's own <predictive-search-component>
 * (that component lives only in Horizon's hidden stock header). Shared by
 * hygeon-header-main.liquid, hygeon-header-sticky.liquid and
 * hygeon-header-nav-mobile.liquid rather than duplicated per header.
 *
 * 2026-09-06: originally built to only search on submit (Enter / the
 * search button), matching hygeon.de's own real search at the time — no
 * live-as-you-type.
 *
 * CHANGED 2026-09-22 (explicit user instruction): "the filter should work
 * on the begin when i am typing", not only after Enter/the button — a
 * deliberate deviation from that 2026-09-06 match-the-source decision,
 * not a bug fix to it. Now searches live while typing (debounced 300ms,
 * from 2 characters), the same behavior most real search boxes use.
 * Clicking a product inside the results was already a real
 * <a href="..."> straight to that product's own page — unchanged, still
 * works.
 *
 * CHANGED 2026-09-29 (real bug reported: "in search bar when write
 * nothing happens"). Root cause: submit (Enter, or the search button)
 * called event.preventDefault() and just re-ran the SAME live-typing
 * lookup, so pressing Enter never did anything visibly different from
 * what was already on screen — no navigation, ever, not even to the
 * real /search results page (which by now has real AND-matching,
 * synonyms, filters, and the exact-category redirect — none of which
 * this small 6-item dropdown reimplements or ever will). Removed
 * preventDefault() from the submit handler entirely: submit now lets
 * the form's own real action="/search" method="get" run natively, the
 * same plain navigation any other search form on the web does. The
 * live-as-you-type dropdown above is unchanged — still shows instant
 * suggestions while typing — this only fixes what happens the moment
 * someone actually submits.
 */
(function () {
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatPrice(value) {
    var amount = parseFloat(value || '0');
    if (isNaN(amount)) amount = 0;
    return amount.toFixed(2).replace('.', ',') + '&nbsp;€';
  }

  function attach(form, input, resultsEl) {
    if (!form || !input || !resultsEl || form.dataset.hygeonSearchOverlayAttached) return;
    form.dataset.hygeonSearchOverlayAttached = 'true';

    var activeController = null;

    function close() {
      resultsEl.hidden = true;
      resultsEl.innerHTML = '';
    }

    function render(term, products) {
      var viewAllUrl = '/search?q=' + encodeURIComponent(term);
      var safeTerm = escapeHtml(term);

      if (!products.length) {
        resultsEl.innerHTML =
          '<p class="hygeon-search-overlay__empty">Keine Ergebnisse f&uuml;r &bdquo;' +
          safeTerm +
          '&ldquo; gefunden. &Uuml;berpr&uuml;fe die Schreibweise oder versuche es mit einer anderen Suchanfrage.</p>' +
          '<a class="hygeon-search-overlay__viewall" href="' +
          viewAllUrl +
          '">Alle Ergebnisse anzeigen</a>';
        resultsEl.hidden = false;
        return;
      }

      var itemsHtml = products
        .map(function (product) {
          var img = product.image
            ? product.image + (product.image.indexOf('?') > -1 ? '&' : '?') + 'width=96'
            : '';
          var thumb = img
            ? '<img class="hygeon-search-overlay__thumb" src="' + img + '" alt="" loading="lazy">'
            : '<div class="hygeon-search-overlay__thumb"></div>';
          // 2026-09-18: real prices restored for everyone.
          var priceHtml = Number(product.price) > 0 ? formatPrice(product.price) : 'Preis auf Anfrage';
          return (
            '<a class="hygeon-search-overlay__item" href="' +
            product.url +
            '">' +
            thumb +
            '<span class="hygeon-search-overlay__info">' +
            '<span class="hygeon-search-overlay__title">' +
            escapeHtml(product.title) +
            '</span>' +
            '<span class="hygeon-search-overlay__price">' +
            priceHtml +
            '</span>' +
            '</span>' +
            '</a>'
          );
        })
        .join('');

      resultsEl.innerHTML =
        itemsHtml +
        '<a class="hygeon-search-overlay__viewall" href="' + viewAllUrl + '">Alle Ergebnisse anzeigen</a>';
      resultsEl.hidden = false;
    }

    function run(term) {
      if (!term) {
        close();
        return;
      }

      if (activeController) activeController.abort();
      activeController = new AbortController();

      fetch(
        '/search/suggest.json?q=' + encodeURIComponent(term) + '&resources[type]=product&resources[limit]=6',
        { signal: activeController.signal, headers: { Accept: 'application/json' } }
      )
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          render(term, products);
        })
        .catch(function (err) {
          if (err && err.name !== 'AbortError') close();
        });
    }

    var debounceTimer = null;

    // 2026-09-29: no preventDefault here — submit (Enter / the search
    // button) is a real navigation to the form's own action="/search",
    // not another round of the live-typing dropdown. See file header.
    form.addEventListener('submit', function () {
      close();
    });

    input.addEventListener('input', function () {
      if (debounceTimer) clearTimeout(debounceTimer);
      var term = input.value.trim();
      if (term.length < 2) {
        close();
        return;
      }
      debounceTimer = setTimeout(function () {
        run(term);
      }, 300);
    });

    document.addEventListener('click', function (event) {
      if (!form.contains(event.target)) close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') close();
    });
  }

  window.HygeonSearchOverlay = { attach: attach };
})();
